import React, { useEffect, useState } from "react";
import { LookSelector } from "./LookSelector";
import { LookInfo } from "../../handlers/types";
import { openInvoice } from "@telegram-apps/sdk";
import "./CharacterLooks.css";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { useGameStore, selectLooks, selectSelectedLookId, selectIsInitialized } from "../../store/gameStore";
import Game from "../../scripts/game/Game";
import ApiService from "../../handlers/apiService";

interface CharacterLooksProps {
  characterId: string;
  isEmbedded?: boolean;
  onComplete?: () => void;
}

export const CharacterLooks: React.FC<CharacterLooksProps> = ({
  characterId,
  isEmbedded = false,
  onComplete,
}) => {
  const { t } = useI18n();
  const storeLooks = useGameStore(selectLooks);
  const storeSelectedLookId = useGameStore(selectSelectedLookId);

  const isInitialized = useGameStore(selectIsInitialized);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check initialization
  useEffect(() => {
    if (!isInitialized) {
      setError(t("character.looks.errorService") || "Store not initialized");
    } else if (storeLooks.length === 0) {
      setError(t("character.looks.errorLoading") || "No looks available");
    } else {
      setError(null);
    }
  }, [isInitialized, storeLooks, t]);

  const handleSelectLook = async (look: LookInfo) => {
    try {
      setIsLoading(true);
      // Use the store action to select the look
      const success = await useGameStore.getState().selectLook(look.id);

      if (success) {
        // Call onComplete if provided after successfully selecting the look
        if (onComplete) {
          setTimeout(() => {
            onComplete();
          }, 300); // Small delay for better UX
        }
      } else {
        setError(t("character.looks.errorSetting") || "Failed to set look");
      }
    } catch (err) {
      console.error("Failed to set active look:", err);
      setError(t("character.looks.errorSetting") || "Failed to set look");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchaseLook = async (look: LookInfo) => {
    try {
      setIsLoading(true);
      setError(null);

      // Call the API to get purchase invoice
      const api = ApiService.getInstance();
      const response = await api.api.looksBuyCreate(look.id);
      const { invoiceUrl } = response;

      // Check if invoice URL is available and can be opened
      if (invoiceUrl && openInvoice.isAvailable()) {
        const status = await openInvoice(invoiceUrl, "url");

        if (status === "paid") {
          // Wait for 1 second before using the boost
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Refresh the looks data from the store
          await useGameStore.getState().refreshLooks();

          // Select the newly purchased look
          await handleSelectLook(look);
        } else if (status === "failed") {
          setError(
            t("character.looks.errorPayment") ||
              "Payment failed. Please try again."
          );
        }
        // For cancelled payments, just keep the popup open
      } else {
        setError(
          t("character.looks.errorInvoice") ||
            "Payment is not available at the moment."
        );
      }
    } catch (err) {
      console.error("Failed to purchase look:", err);
      setError(t("character.looks.errorPurchase") || "Failed to purchase look");
    } finally {
      setIsLoading(false);
    }
  };

    const containerClass = isEmbedded
      ? "character-looks character-looks--embedded"
      : "character-looks";

    const skeletonCount = 12;

    // Render loading skeletons
    if (isLoading) {
    return (
      <div className={containerClass}>
        {!isEmbedded && (
          <div className="character-looks__header">
            <h1 className="character-looks__title">
              {t("character.looks.title") || "Apply a look"}
            </h1>
            <p className="character-looks__subtitle">
              {t("character.looks.subtitle") ||
                "Select look. Characters with rare looks will be valued more when you sell them."}
            </p>
          </div>
        )}

        <div className="character-looks__grid">
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="character-looks__grid-item"
            >
              <div className="character-looks__skeleton">
                <div className="character-looks__skeleton-image"></div>
                <div className="character-looks__skeleton-title"></div>
                <div className="character-looks__skeleton-button"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Calculate how many empty slots to render to keep grid layout clean
  const emptySlotCount = Math.max(0, skeletonCount - storeLooks.length);

  return (
    <div className={containerClass}>
      {!isEmbedded && (
        <div className="character-looks__header">
          <h1 className="character-looks__title">
            {t("character.looks.title") || "Apply a look"}
          </h1>
          <p className="character-looks__subtitle">
            {t("character.looks.subtitle") ||
              "Select look. Characters with rare looks will be valued more when you sell them."}
          </p>
          {error && <p className="character-looks__error">{error}</p>}
        </div>
      )}
      {isEmbedded && error && (
        <p className="character-looks__error character-looks__error--embedded">
          {error}
        </p>
      )}

      <div className="character-looks__grid">
        {storeLooks.map((look) => {
          return (
            <div key={look.id} className="character-looks__grid-item">
              <LookSelector
                look={look}
                isSelected={storeSelectedLookId === look.id}
                onSelect={handleSelectLook}
                onPurchase={handlePurchaseLook}
              />
            </div>
          );
        })}

        {/* Add empty placeholder slots to fill the grid */}
        {Array.from({ length: emptySlotCount }).map((_, index) => (
          <div
            key={`empty-${index}`}
            className="character-looks__grid-item character-looks__grid-item--empty"
          >
            <div className="character-looks__empty-slot">?</div>
          </div>
        ))}
      </div>
    </div>
  );
};
